import { z } from "zod";
import { defineEvent } from "./_define";
import { baseEnvelopeSchema } from "./_envelope";

export const VISIT_REQUEST_STARTED_VALUES = {
  TRIAGE: "triage",
  MANUAL: "manual",
  DEEP_LINK: "deep_link",
} as const;
export type VisitRequestStartedValue =
  (typeof VISIT_REQUEST_STARTED_VALUES)[keyof typeof VISIT_REQUEST_STARTED_VALUES];

export const VISIT_CANCELLED_VALUES = {
  PATIENT_REQUEST: "patient_request",
  PROVIDER_UNAVAILABLE: "provider_unavailable",
  DUPLICATE_CASE: "duplicate_case",
  OUT_OF_SCOPE: "out_of_scope",
  TECHNICAL_ERROR: "technical_error",
  TIMEOUT: "timeout",
  OTHER: "other",
} as const;
export type VisitCancelledValue =
  (typeof VISIT_CANCELLED_VALUES)[keyof typeof VISIT_CANCELLED_VALUES];

const providerSchema = z.enum(["mdi", "blueberry"]).or(z.string());

const visitEnvelope = baseEnvelopeSchema.extend({
  provider: providerSchema.optional(),
  subscription_status: z.string().optional(),
  is_during_trial: z.union([z.string(), z.boolean()]).optional(),
  child_id_hash: z.string().optional(),
  journey_id: z.string().optional(),
});

export const visitRequestStarted = defineEvent({
  eventName: "visit_request_started",
  eventVersion: 2,
  valueSchema: z.enum([
    VISIT_REQUEST_STARTED_VALUES.TRIAGE,
    VISIT_REQUEST_STARTED_VALUES.MANUAL,
    VISIT_REQUEST_STARTED_VALUES.DEEP_LINK,
  ]),
  metadataSchema: visitEnvelope.passthrough(),
});

export const visitRequestCancelled = defineEvent({
  eventName: "visit_request_cancelled",
  eventVersion: 1,
  valueSchema: z.union([z.string(), z.number()]).optional(),
  metadataSchema: visitEnvelope.passthrough(),
});

export const visitCompleted = defineEvent({
  eventName: "visit_completed",
  eventVersion: 2,
  valueSchema: z.union([z.string(), z.number()]).optional(),
  metadataSchema: visitEnvelope
    .extend({
      n_visits_lifetime: z.union([z.string(), z.number()]).optional(),
      visit_duration_ms: z.union([z.string(), z.number()]).optional(),
    })
    .passthrough(),
});

export const visitCancelled = defineEvent({
  eventName: "visit_cancelled",
  eventVersion: 2,
  valueSchema: z.enum([
    VISIT_CANCELLED_VALUES.PATIENT_REQUEST,
    VISIT_CANCELLED_VALUES.PROVIDER_UNAVAILABLE,
    VISIT_CANCELLED_VALUES.DUPLICATE_CASE,
    VISIT_CANCELLED_VALUES.OUT_OF_SCOPE,
    VISIT_CANCELLED_VALUES.TECHNICAL_ERROR,
    VISIT_CANCELLED_VALUES.TIMEOUT,
    VISIT_CANCELLED_VALUES.OTHER,
  ]),
  metadataSchema: visitEnvelope.passthrough(),
});

export const visitActive = defineEvent({
  eventName: "visit_active",
  eventVersion: 1,
  valueSchema: z.union([z.string(), z.number()]).optional(),
  metadataSchema: visitEnvelope.passthrough(),
});

export const visitProviderAssigned = defineEvent({
  eventName: "visit_provider_assigned",
  eventVersion: 1,
  valueSchema: z.union([z.string(), z.number()]).optional(),
  metadataSchema: visitEnvelope.passthrough(),
});

export const caseCreated = defineEvent({
  eventName: "case_created",
  eventVersion: 1,
  valueSchema: z.enum(["visit", "rx_only", "message_only", "unknown"]).or(z.string()),
  metadataSchema: visitEnvelope.passthrough(),
});

export const conditionSelected = defineEvent({
  eventName: "condition_selected",
  eventVersion: 1,
  valueSchema: z.string(),
  metadataSchema: baseEnvelopeSchema.passthrough(),
});

export const childSelector = defineEvent({
  eventName: "child_selector",
  eventVersion: 1,
  valueSchema: z.enum(["triggered", "auto_skipped", "dismissed", "confirmed"]),
  metadataSchema: baseEnvelopeSchema
    .extend({
      n_children: z.union([z.string(), z.number()]).optional(),
      selected_child_index: z.union([z.string(), z.number()]).optional(),
      time_to_select_ms: z.union([z.string(), z.number()]).optional(),
      chat_intent: z.union([z.string(), z.boolean()]).optional(),
    })
    .passthrough(),
});

export const chatMessageSent = defineEvent({
  eventName: "chat_message_sent",
  eventVersion: 1,
  valueSchema: z.enum(["parent_to_provider", "provider_to_parent"]),
  metadataSchema: visitEnvelope
    .extend({
      chat_thread_age_days: z.union([z.string(), z.number()]).optional(),
      is_first_message_in_thread: z.union([z.string(), z.boolean()]).optional(),
    })
    .passthrough(),
});

export const chatFirstResponseReceived = defineEvent({
  eventName: "chat_first_response_received",
  eventVersion: 1,
  valueSchema: z.number().int().nonnegative(),
  metadataSchema: visitEnvelope.passthrough(),
});
